import React, { useState, useEffect } from 'react';

const UsernameModal = ({ isOpen, onClose, onSubmit, currentUsername = '' }) => {
  const [username, setUsername] = useState(currentUsername);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate username
    if (!username || username.trim().length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    
    if (username.trim().length > 50) {
      setError('Username must be less than 50 characters');
      return;
    }
    
    // Only allow alphanumeric, spaces, and basic characters
    if (!/^[a-zA-Z0-9\s._-]+$/.test(username)) {
      setError('Username can only contain letters, numbers, spaces, dots, underscores, and hyphens');
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      await onSubmit(username.trim());
    } catch (err) {
      setError(err.message || 'Failed to update username');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-[#1a1a1a] rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        <h2 className="text-2xl font-bold text-white mb-4">Choose Your Username</h2>
        <p className="text-gray-300 mb-6">
          Please enter a username for your account. This will be displayed on your submissions.
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="username" className="block text-gray-300 mb-2 font-medium">
              Username *
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 bg-[#0F0F0F] border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              placeholder="Enter your username"
              disabled={loading}
              autoFocus
              maxLength={50}
            />
            {error && (
              <p className="mt-2 text-red-500 text-sm">{error}</p>
            )}
            <p className="mt-2 text-gray-400 text-sm">
              {username.length}/50 characters
            </p>
          </div>
          
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !username.trim()}
              className="flex-1 bg-gradient-to-r from-cyan-600 to-pink-600 text-white font-bold py-2 px-4 rounded-lg hover:from-cyan-700 hover:to-pink-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsernameModal;
